import React, {FC, useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap-v5'
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'
import {publicJobUrl} from './JobModel'
import {buildJobPost, IShareableJob} from './JobShare'

type Props = {
  job: (IShareableJob & {status?: string}) | null
  onClose: () => void
}

type Platform = {
  key: string
  label: string
  icon: string
  color: string
}

const PLATFORMS: Platform[] = [
  {key: 'linkedin', label: 'LinkedIn', icon: 'bi-linkedin', color: '#0a66c2'},
  {key: 'instagram', label: 'Instagram', icon: 'bi-instagram', color: '#e1306c'},
  {key: 'facebook', label: 'Facebook', icon: 'bi-facebook', color: '#1877f2'},
  {key: 'whatsapp', label: 'WhatsApp', icon: 'bi-whatsapp', color: '#25d366'},
]

const openWindow = (url: string) => window.open(url, '_blank', 'noopener,noreferrer')

/**
 * İlanı paylaş: düzenlenebilir paylaşım metni + platform butonları.
 *
 * Platformların paylaşım adresleri metni farklı destekler: LinkedIn (feed) ve WhatsApp metni
 * doğrudan alır; Facebook yalnızca linki alır, Instagram web'den paylaşıma izin vermez.
 * Bu yüzden her paylaşımda metin panoya da kopyalanır ve kullanıcı gerekirse yapıştırır.
 */
export const ShareJobModal: FC<Props> = ({job, onClose}) => {
  const [text, setText] = useState('')

  useEffect(() => {
    if (job) setText(buildJobPost(job))
  }, [job])

  if (!job) return null

  const url = publicJobUrl(job.id)
  const isPublished = !job.status || job.status === 'ACTIVE'

  const copy = async (value: string, message: string) => {
    try {
      await navigator.clipboard.writeText(value)
      showSuccessToast(message)
      return true
    } catch (error) {
      showErrorToast('Panoya kopyalanamadı')
      return false
    }
  }

  const share = async (platform: string) => {
    switch (platform) {
      case 'linkedin':
        await copy(text, 'Metin panoya kopyalandı; LinkedIn alanı boş gelirse yapıştırabilirsiniz')
        openWindow(`https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(text)}`)
        break
      case 'facebook':
        await copy(text, 'Metin panoya kopyalandı; Facebook gönderisine yapıştırın')
        openWindow(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`)
        break
      case 'whatsapp':
        openWindow(`https://wa.me/?text=${encodeURIComponent(text)}`)
        break
      case 'instagram':
        // Instagram web'den gönderi paylaşımına izin vermez; mobilde sistem paylaşımı kullanılır
        if (navigator.share) {
          try {
            await navigator.share({title: job.title, text, url})
            return
          } catch {
            // Kullanıcı iptal ettiyse aşağıdaki yönteme düşülür
          }
        }
        await copy(text, 'Metin panoya kopyalandı; Instagram gönderisine ya da hikayesine yapıştırın')
        openWindow('https://www.instagram.com/')
        break
    }
  }

  return (
    <Modal show={!!job} onHide={onClose} size='lg' dialogClassName='modal-dialog-centered'>
      <div className='modal-header'>
        <h5 className='modal-title'>İlanı Paylaş</h5>
        <button type='button' className='btn-close' onClick={onClose} />
      </div>
      <div className='modal-body'>
        {!isPublished && (
          <div className='alert alert-warning'>
            İlan şu anda yayında değil; paylaşılan bağlantı adaylara "İlan bulunamadı" gösterir. Önce ilanı
            Yayında yapınız.
          </div>
        )}

        <label className='form-label fw-bold'>Paylaşım Metni</label>
        <textarea
          className='form-control mb-2'
          rows={14}
          value={text}
          onChange={(event) => setText(event.target.value)}
        />
        <div className='d-flex justify-content-between align-items-center mb-8'>
          <span className='text-muted fs-7'>Metni paylaşmadan önce düzenleyebilirsiniz.</span>
          <button type='button' className='btn btn-sm btn-link p-0' onClick={() => setText(buildJobPost(job))}>
            Varsayılana döndür
          </button>
        </div>

        <div className='d-flex flex-wrap gap-3 mb-6'>
          {PLATFORMS.map((platform) => (
            <button
              key={platform.key}
              type='button'
              className='btn text-white d-inline-flex align-items-center gap-2'
              style={{backgroundColor: platform.color}}
              onClick={() => share(platform.key)}
            >
              <i className={`bi ${platform.icon} text-white fs-4`}></i>
              {platform.label}
            </button>
          ))}
        </div>

        <label className='form-label fw-bold'>İlan Bağlantısı</label>
        <div className='input-group'>
          <input type='text' className='form-control' value={url} readOnly onFocus={(event) => event.target.select()} />
          <button type='button' className='btn btn-light-primary' onClick={() => copy(url, 'Bağlantı kopyalandı')}>
            <i className='bi bi-link-45deg'></i>
            Linki Kopyala
          </button>
          <button type='button' className='btn btn-light' onClick={() => copy(text, 'Metin kopyalandı')}>
            <i className='bi bi-clipboard'></i>
            Metni Kopyala
          </button>
        </div>
      </div>
    </Modal>
  )
}
